import type {
  Territory,
  ViewReportPrams,
  ViewTerritory,
} from "@/services/api/territoryMaster";
import TerritoryService from "@/services/api/territoryMaster";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LoadingState {  
  territories: boolean;
  territoryReport: boolean; 
}

interface RoleState {
  territories: Territory[];
  parentTerritories: Territory[];
  totalTerritories: number;
  isFetching: boolean;
  error: string | null;
  territoriesReport: { Items?: any[]; TotalRecords?: number };
  viewReport: { Items?: ViewTerritory[]; TotalRecords?: number };
  loading: LoadingState;  
}

const initialState: RoleState = {
  territories: [],
  parentTerritories: [],
  totalTerritories: 0,
  isFetching: false,
  error: null,
  territoriesReport: {
    Items: [],
    TotalRecords: 0,
  },
  viewReport: {
    Items: [],
    TotalRecords: 0,
  },
  loading: {  
    territories: false,
    territoryReport: false,
  },
};

export const getTerritories = createAsyncThunk(
  "territory/getTerritories",
  async (params : any
    , { rejectWithValue }) => {
    try {
      const response = await TerritoryService.getTerritories(params);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.Message || "Failed to fetch territories");
    }
  },
);

export const getParentTerritories = createAsyncThunk(
  "territory/getParentTerritories",
  async (params, { rejectWithValue }) => {
    try {
      const response = await TerritoryService.getParentTerritories(params);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || "Failed to fetch parent territories",
      );
    }
  },
);

export const downloadAllTerritories = createAsyncThunk(
  "territories/downloadAllTerritories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TerritoryService.downloadExcelFile();
      return saveAsExcelFile(response, "Territory");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to download All Territories",
      );
    }
  },
);

export const TerritoryBulkUpload = createAsyncThunk(
  "TerritoryMaster/territoryBulkUpload",
  async (payload: any = undefined, { rejectWithValue }) => {
    try {
      const res = await TerritoryService.bulkUploadTerritory(payload);
      return res;
    } catch (err: any) {
      return rejectWithValue(err?.Message);
    }
  },
);

export const fetchTerritoryReport = createAsyncThunk(
  "TerritoryMaster/fetchTerritoryReport",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await TerritoryService.fetchTerritoryReport(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to fetch territory report data",
      );
    }
  },
);

export const exportBulkUploadReport = createAsyncThunk(
  "TerritoryMaster/exportBulkUploadReport",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await TerritoryService.exportBulkUploadReport(fileId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to export bulk upload report",
      );
    }
  },
);

export const exportBulkUploadFile = createAsyncThunk(
  "TerritoryMaster/exportBulkUploadFile",
  async (fileId: string, { rejectWithValue }) => {
    try {
      const response = await TerritoryService.exportBulkUploadFile(fileId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.Message ?? "Failed to export bulk upload file",
      );
    }
  },
);

export const ViewTerritoryReport = createAsyncThunk(
  "TerritoryMaster/viewTerritoryReport",
  async (params: ViewReportPrams, { rejectWithValue }) => {
    try {
      const response = await TerritoryService.getViewTerritoryReport(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.Message ?? "Failed to view report");
    }
  },
);

const territorySlice = createSlice({
  name: "territory",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTerritories.pending, (state, action) => {
        state.loading.territories = true;
      })
      .addCase(getTerritories.fulfilled, (state, action) => {
        state.loading.territories = false;
        state.territories = action.payload.Territory;
        state.totalTerritories = action.payload.TotalRecords;
      })
      .addCase(getTerritories.rejected, (state, action) => {
        state.loading.territories = false;
        state.error = action.payload as string;
      })
      .addCase(getParentTerritories.pending, (state, action) => {
        state.isFetching = true;
      })
      .addCase(getParentTerritories.fulfilled, (state, action) => {
        state.isFetching = false;
        state.parentTerritories = action.payload;
      })
      .addCase(getParentTerritories.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTerritoryReport.pending, (state, action) => {
        state.isFetching = true;
      })
      .addCase(fetchTerritoryReport.fulfilled, (state, action) => {
        state.isFetching = false;
        state.territoriesReport = action.payload;
      })
      .addCase(fetchTerritoryReport.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      .addCase(ViewTerritoryReport.pending, (state, action) => {
        state.isFetching = true;
      })
      .addCase(ViewTerritoryReport.fulfilled, (state, action) => {
        state.isFetching = false;
        state.viewReport = action.payload;
      })
      .addCase(ViewTerritoryReport.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      .addCase(downloadAllTerritories.pending, (state, action) => {
         state.loading.territoryReport = true;
      })
      .addCase(downloadAllTerritories.fulfilled, (state, action) => {
        state.loading.territoryReport = false;
      })
      .addCase(downloadAllTerritories.rejected, (state, action) => {
        state.loading.territoryReport = false;
        state.error = action.payload as string;
      });
  },
});

export default territorySlice.reducer;
